/**
 * Safely get a nested property from an object
 * @param obj The object to get the property from
 * @param path The path to the property (can be dot notation or array)
 * @param defaultValue The default value to return if the property doesn't exist
 */
export const get = <T = any>(
  obj: any,
  path: string | string[],
  defaultValue?: T
): T => {
  if (!obj) return defaultValue as T;
  
  const pathArray = Array.isArray(path) ? path : path.split('.');
  let result = obj;
  
  for (const key of pathArray) {
    if (result === undefined || result === null) return defaultValue as T;
    result = result[key];
  }
  
  return result === undefined ? defaultValue as T : result;
};

/**
 * Creates an array of values by running each element in collection through iteratee
 * @param collection The collection to iterate over
 * @param iteratee The function invoked per iteration
 */
export const map = <T, U>(
  collection: T[] | null | undefined,
  iteratee: (value: T, index: number, array: T[]) => U
): U[] => {
  if (!collection) return [];
  return collection.map(iteratee);
};

/**
 * Converts the first character of string to upper case
 * @param str The string to convert
 */
export const upperFirst = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

export const toNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

export const takeRight = <T>(array: T[], n: number = 1): T[] => {
  if (!Array.isArray(array)) return [];
  return array.slice(-n);
}; 